import { parseDate } from 'lib/date';

import {
  ActionStatus,
  toGroup,
  WorkflowAction,
  WorkflowActionOptions,
  WorkflowGroupOptions,
  WorkflowInputDiff,
} from './WorkflowAction';
import WorkflowActionModelFactory from './WorkflowActionModelFactory';

// Re-export the action model + its types so `models/Workflow` stays the one import surface for
// the workflow domain (WorkflowAction now lives in its own file, per the per-kind action models).
export * from './WorkflowAction';

// Lifecycle status of the whole run (design §11). Kept as a string union so an
// unknown future status from the API degrades gracefully rather than throwing.
export enum WorkflowStatus {
  RUNNING = 'running',
  ACTION_REQUIRED = 'action_required',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

// `outcome` is a free-form string the engine never interprets (design §11);
// referral intake writes accepted / rejected, undecided is "none".
export const NO_OUTCOME = 'none';

// The packet value name (mirrors the API's Workflow::ATTACHMENTS_VALUE_NAME): replacing
// the packet is a change to this value, which is how it enters the impact derivation.
export const ATTACHMENTS_VALUE_NAME = 'attachments';

// Statuses a person can meaningfully restart — the step ran, is running, or is parked on
// a person. Pending/flagged steps never started (nothing to restart, and run_action on
// them would churn instances for no reason); canceled/skipped won't run again.
const RESTARTABLE_STATUSES = new Set<ActionStatus>([
  ActionStatus.COMPLETED,
  ActionStatus.ACTION_REQUIRED,
  ActionStatus.RUNNING,
  ActionStatus.ERRORED,
]);

export interface WorkflowCategoryOptions {
  key: string;
  name: string;
  position: number;
}

export interface WorkflowDefinitionOptions {
  apiName: string;
  displayName: string;
  categories: WorkflowCategoryOptions[];
}

export interface WorkflowValueOptions {
  name: string;
  value: string | null;
  source: string;
  groupId: string | null;
  // The catalog display label (design P1-5); null when the API hasn't supplied one.
  label: string | null;
}

// The current packet (the attachments behind the attachment_ids value), so the edit
// screen can show what's attached before replacing it.
export interface WorkflowAttachmentOptions {
  id: string;
  filename: string | null;
  docType: string | null;
}

// The buildings a machine-collected retrieval matched the referral to — the
// `candidate_facilities` value's array entries. Keys are snake_case: this is value
// JSON, not an API field, so OliveBranch leaves it alone.
export interface CandidateFacility {
  id: string;
  name: string;
  group_type: string;
}

export interface WorkflowOptions {
  id: string;
  status: WorkflowStatus;
  outcome: string;
  // How the retrieval step runs (portal_automation only): manual | extension.
  retrievalMode?: string;
  createdAt: string | Date | null;
  // Who created the run — the header's "by <name>"; null for machine-created runs.
  createdByName?: string | null;
  definition: Partial<WorkflowDefinitionOptions>;
  actions: Partial<WorkflowActionOptions>[];
  values: Partial<WorkflowValueOptions>[];
  groups: Partial<WorkflowGroupOptions>[];
  attachments: Partial<WorkflowAttachmentOptions>[];
}

function getDefaults(): WorkflowOptions {
  return {
    id: '',
    status: WorkflowStatus.RUNNING,
    outcome: NO_OUTCOME,
    createdAt: null,
    definition: {},
    actions: [],
    values: [],
    groups: [],
    attachments: [],
  };
}

export class WorkflowValue {
  name: string;
  value: string | null;
  source: string;
  groupId: string | null;
  label: string | null;

  constructor(opts: Partial<WorkflowValueOptions> = {}) {
    this.name = opts.name ?? '';
    this.value = opts.value ?? null;
    this.source = opts.source ?? '';
    this.groupId = opts.groupId ?? null;
    this.label = opts.label ?? null;
  }
}

export class WorkflowDefinition {
  apiName: string;
  displayName: string;
  categories: WorkflowCategoryOptions[];

  constructor(opts: Partial<WorkflowDefinitionOptions> = {}) {
    this.apiName = opts.apiName ?? '';
    this.displayName = opts.displayName ?? '';
    this.categories = [...(opts.categories ?? [])].sort((a, b) => a.position - b.position);
  }
}

// Render an input value for the staleness diff: packet values are arrays of
// `{id, filename}` objects, so show their filenames; scalar values stringify. Returns
// null for an absent value so the diff can render an em dash.
function formatInputValue(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const formatted = value
      .map((entry) =>
        entry && typeof entry === 'object' && 'filename' in entry
          ? String((entry as { filename: string | null }).filename ?? '')
          : String(entry)
      )
      .filter((part) => part.length > 0)
      .join(', ');
    return formatted.length > 0 ? formatted : null;
  }
  return String(value);
}

export default class Workflow {
  id: string;
  status: WorkflowStatus;
  outcome: string;
  retrievalMode?: string;
  createdAt: Date | null;
  createdByName: string | null;
  definition: WorkflowDefinition;
  actions: WorkflowAction[];
  values: WorkflowValue[];
  groups: WorkflowGroupOptions[];
  attachments: WorkflowAttachmentOptions[];

  constructor(options: Partial<WorkflowOptions> = {}) {
    const opts = { ...getDefaults(), ...options };
    this.id = opts.id;
    this.status = opts.status;
    this.outcome = opts.outcome;
    this.retrievalMode = opts.retrievalMode;
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.createdByName = opts.createdByName ?? null;
    this.definition = new WorkflowDefinition(opts.definition);
    // The API pre-sorts actions by (category position, entry position); keep that order so the
    // checklist reads the same every time (design §12). The factory picks each action's model
    // subclass by kind (e.g. DenialCheckAction), so per-kind behavior comes for free.
    this.actions = opts.actions.map((a) => WorkflowActionModelFactory.get(a));
    this.values = opts.values.map((v) => new WorkflowValue(v));
    this.groups = opts.groups.map(toGroup);
    this.attachments = opts.attachments.map((a) => ({
      id: a.id ?? '',
      filename: a.filename ?? null,
      docType: a.docType ?? null,
    }));
  }

  // Terminal runs are frozen except the outcome (design §11) — used to stop polling.
  get isTerminal(): boolean {
    return this.status === WorkflowStatus.COMPLETED || this.status === WorkflowStatus.CANCELED;
  }

  get isCompleted(): boolean {
    return this.status === WorkflowStatus.COMPLETED;
  }

  get hasOutcome(): boolean {
    return !!this.outcome && this.outcome !== NO_OUTCOME;
  }

  // No dedicated patient-name value; compose it from the demographic values,
  // falling back to a neutral label before any data is entered.
  get patientName(): string {
    const first = this.valueFor('patient_first_name')?.value ?? '';
    const last = this.valueFor('patient_last_name')?.value ?? '';
    return `${first} ${last}`.trim() || 'New referral';
  }

  get groupNames(): string[] {
    return this.groups.map((g) => g.name).filter(Boolean);
  }

  // Coarse progress for the list page: completed vs. total expected actions.
  get progress(): { completed: number; total: number } {
    return {
      completed: this.actions.filter((a) => a.isCompleted).length,
      total: this.actions.length,
    };
  }

  // The winning value for a name, resolved group-contextually like the API does
  // (design §5): prefer the building's own row, fall back to the shared (ungrouped)
  // one. Pass the reading action's groupId so per-building lanes never read a
  // sibling building's value. A null groupId (or none) reads the shared row first,
  // tolerating a lone group row for legacy callers.
  // Whether patientName is real data (vs. its pre-data fallback label) — key off this
  // rather than comparing against the fallback copy.
  get hasPatientName(): boolean {
    const first = this.valueFor('patient_first_name')?.value ?? '';
    const last = this.valueFor('patient_last_name')?.value ?? '';
    return Boolean(`${first} ${last}`.trim());
  }

  // The `candidate_facilities` value, narrowed defensively — the model types values as
  // strings but this one is an array of facility objects; anything malformed reads as [].
  candidateFacilities(): CandidateFacility[] {
    const raw = this.valueFor('candidate_facilities')?.value as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map((entry) => ({
        id: String(entry.id ?? ''),
        name: String(entry.name ?? ''),
        group_type: String(entry.group_type ?? ''),
      }))
      .filter((facility) => facility.id);
  }

  valueFor(name: string, groupId: string | null = null): WorkflowValue | undefined {
    const named = this.values.filter((v) => v.name === name);
    if (groupId) return named.find((v) => v.groupId === groupId) ?? named.find((v) => v.groupId === null);
    return named.find((v) => v.groupId === null) ?? named[0];
  }

  // Resolve "current" through `valueFor` (not a fresh name->value map) so the diff
  // agrees with the pill shown elsewhere when a name is duplicated across
  // workflow-level + group-scoped values (P1-1 contract §2).
  inputDiffFor(action: WorkflowAction): WorkflowInputDiff[] {
    return (
      action.inputs
        .map((input) => ({
          name: input.name,
          ranWithRaw: input.value as unknown,
          currentRaw: (this.valueFor(input.name, action.groupId)?.value ?? null) as unknown,
        }))
        // Deep-compare (object/array values aren't reference-equal), so an identical
        // packet doesn't read as changed.
        .filter((d) => JSON.stringify(d.ranWithRaw ?? null) !== JSON.stringify(d.currentRaw ?? null))
        .map((d) => ({
          name: d.name,
          ranWith: formatInputValue(d.ranWithRaw),
          current: formatInputValue(d.currentRaw),
        }))
    );
  }

  // The transitive impact of changing these value names (design §9) — the consent list
  // for the correction cascade. Walks the declared contracts the API already serves
  // (required/produces per action): an action consuming an affected name is impacted,
  // its produced names become affected, repeat to a fixed point. Every action's contract
  // propagates the ripple (a pending step still passes it along), but only steps that
  // actually started are worth restarting, so the returned list is filtered to those.
  impactedActionsBy(changedNames: string[]): WorkflowAction[] {
    const affected = new Set(changedNames);
    const impactedIds = new Set<string>();
    let grew = true;
    while (grew) {
      grew = false;
      this.actions.forEach((action) => {
        if (!action.required.some((name) => affected.has(name))) return;
        if (!impactedIds.has(action.id)) {
          impactedIds.add(action.id);
          grew = true;
        }
        action.produces.forEach((name) => {
          if (!affected.has(name)) {
            affected.add(name);
            grew = true;
          }
        });
      });
    }
    return this.actions.filter((a) => impactedIds.has(a.id) && RESTARTABLE_STATUSES.has(a.status));
  }

  // Producers of this action's required inputs whose current instance is still in flight
  // (pending or running) — the mid-redo awareness signal (design §9/§13). The person can
  // still act on this step; the notice just makes sure they do it knowingly, because an
  // input may be about to change.
  inFlightProducersFor(action: WorkflowAction): WorkflowAction[] {
    return this.actions.filter(
      (producer) =>
        producer.id !== action.id &&
        // Only RE-RUN producers warn (a first-pass producer isn't redoing anything);
        // pending still counts — a queued redo behind a running sibling is in flight.
        producer.rerun &&
        // Lane-scoped: a per-building producer only feeds its own building (or a
        // shared producer feeds everyone) — sibling buildings' redos are irrelevant.
        (producer.groupId === null || producer.groupId === action.groupId) &&
        (producer.status === ActionStatus.PENDING || producer.status === ActionStatus.RUNNING) &&
        producer.produces.some((name) => action.required.includes(name))
    );
  }

  // Actions grouped under their category for display, in definition order. Actions
  // whose category is unknown fall into a trailing group so nothing is dropped.
  actionsByCategory(): { category: WorkflowCategoryOptions; actions: WorkflowAction[] }[] {
    return this.definition.categories.map((category) => ({
      category,
      actions: this.actions.filter((a) => a.categoryKey === category.key),
    }));
  }
}
